import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import Button from '../components/Button';

interface Photo {
  id: string;
  s3Key: string;
  order: number;
  isPrimary: boolean;
}

interface Listing {
  _id: string;
  name: string;
  title: string;
  description: string;
  status: string;
  address: string;
  photos?: Photo[];
  hasNoWindowsWarning?: boolean;
  adminDisabled?: boolean;
}

const MyListingsPage: React.FC = () => {
  const [listings, setListings] = useState<Listing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stripeAccountStatus, setStripeAccountStatus] = useState<{
    connected: boolean;
    onboarded: boolean;
    accountId?: string;
  } | null>(null);

  useEffect(() => {
    fetchListings();
    checkStripeAccountStatus();
  }, []);

  const checkStripeAccountStatus = async () => {
    try {
      const status = await api.get<{
        connected: boolean;
        onboarded: boolean;
        accountId?: string;
      }>('/payments/stripe/connect/status');
      setStripeAccountStatus(status);
    } catch {
      // Ignore error - user might not have Stripe account yet
    }
  };

  const fetchListings = async () => {
    try {
      const data = await api.get<{ listings: Listing[] }>('/listings/my/listings');
      setListings(data.listings);
    } catch {
      toast.error('Failed to load listings');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = async (listingId: string) => {
    try {
      const data = await api.post<{ token: string }>(`/listings/${listingId}/preview-token`);
      const previewUrl = `/preview/${listingId}?token=${data.token}`;
      window.open(previewUrl, '_blank');
    } catch {
      toast.error('Failed to generate preview link');
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">My Listings</h1>
        <Link to="/listings/create">
          <Button>Create New Listing</Button>
        </Link>
      </div>

      {/* Stripe Account Status Warnings */}
      {stripeAccountStatus && stripeAccountStatus.connected && !stripeAccountStatus.onboarded && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
          <div className="flex items-center">
            <svg
              className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-yellow-900 mb-1">
                Complete Your Stripe Setup
              </h3>
              <p className="text-yellow-700 mb-3">
                Your Stripe account is connected but onboarding is not complete. You won't be
                able to receive payments until you finish the setup process.
              </p>
              <Link to="/become-owner">
                <Button size="sm" variant="secondary">
                  Complete Stripe Onboarding
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {stripeAccountStatus && !stripeAccountStatus.connected && listings.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
          <div className="flex items-center">
            <svg
              className="w-6 h-6 text-red-600 mr-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-1">
                Stripe Account Disconnected
              </h3>
              <p className="text-red-700 mb-3">
                Your Stripe account is not connected. Your listings cannot receive payments.
                Please reconnect your account to continue receiving bookings.
              </p>
              <Link to="/become-owner">
                <Button size="sm" variant="secondary">
                  Reconnect Stripe Account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {listings.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <p className="text-gray-600 mb-4">You don't have any listings yet.</p>
          <Link to="/listings/create">
            <Button>Create Your First Listing</Button>
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map((listing) => {
            const primaryPhoto = listing.photos?.find(p => p.isPrimary) || listing.photos?.[0];
            const s3BaseUrl = process.env.REACT_APP_S3_BASE_URL || 'https://pay2rentme-images-dev.s3.amazonaws.com';
            
            return (
              <div key={listing._id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <Link to={`/${listing.name}`} className="block">
                  {primaryPhoto ? (
                    <div className="h-48 bg-gray-200 overflow-hidden hover:opacity-90 transition-opacity">
                      <img 
                        src={`${s3BaseUrl}/${primaryPhoto.s3Key}`}
                        alt={listing.title}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors">
                      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </Link>
                <div className="p-4">
                  <Link to={`/${listing.name}`}>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 hover:text-primary-600 transition-colors">
                      {listing.title || listing.name}
                    </h3>
                  </Link>
                  <p className="text-gray-600 mb-2 text-sm line-clamp-2">
                    {listing.description}
                  </p>
                  
                  {/* Warnings */}
                  {(listing.hasNoWindowsWarning || listing.adminDisabled) && (
                    <div className="mb-3 space-y-2">
                      {listing.hasNoWindowsWarning && (
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-2 text-xs">
                          <p className="text-yellow-800 font-medium">
                            This listing is active but has no rental windows. Add windows to accept bookings.
                          </p>
                        </div>
                      )}
                      {listing.adminDisabled && (
                        <div className="bg-red-50 border-l-4 border-red-400 p-2 text-xs">
                          <p className="text-red-800 font-medium">
                            Admin disabled
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span
                      className={`font-medium ${
                        listing.status === 'active' ? 'text-green-600' : 'text-gray-600'
                      }`}
                    >
                      {listing.status}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <Link to={`/listings/${listing._id}/edit`} className="flex-1">
                        <Button size="sm" className="w-full">
                          Edit
                        </Button>
                      </Link>
                      <Link to={`/listings/${listing._id}/windows`} className="flex-1">
                        <Button size="sm" variant="secondary" className="w-full">
                          Windows
                        </Button>
                      </Link>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePreview(listing._id)}
                        className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Preview
                      </button>
                      <Link to={`/listings/${listing._id}/calendar`} className="flex-1">
                        <Button size="sm" variant="secondary" className="w-full">
                          Calendar
                        </Button>
                      </Link>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/listings/${listing._id}/rentals`} className="flex-1">
                        <Button size="sm" variant="secondary" className="w-full">
                          View Rentals
                        </Button>
                      </Link>
                      <Link to={`/listings/${listing._id}/qr-code`} className="flex-1">
                        <Button size="sm" variant="secondary" className="w-full">
                          QR Code
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyListingsPage;

